"use client";

import CaseDetailLabels from "@/components/ui/CaseDetailLabels";
import { newCaseLabelStyle } from "@/utils/case-label-style";
import React, { useState } from "react";
import { CaseSectionProps } from "../model";
import {
  createIcsEvent,
  getCaseDetailSublabel,
  getDefaultCmsSection,
  getOpenHouseText,
} from "@/utils/helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarCirclePlus } from "@fortawesome/sharp-light-svg-icons";
import CaseDetailsImageSlider from "@/components/CaseDetailsImageSlider";
import SectionLayout from "@/components/ui/SectionLayout";
import RippleCtaButton from "@/components/ui/RippleCtaButton";
import {
  ownSectionHeights,
  ownSectionTextColor,
  type OwnSectionHeights,
} from "@/utils/theme-helper";

/** The slider is 400 px high on a phone and 600 px from 768 px; on a device with a height of its own (Højde or Fyld
 *  skærmhøjden) it takes what the section's height leaves it, but not less than 300 px. */
const SLIDER_HEIGHT: Record<OwnSectionHeights, string> = {
  none: "h-[400px] md:h-[600px] xl:h-[600px]",
  both: "min-h-[300px] flex-1",
  mobile: "min-h-[300px] flex-1 md:h-[600px] md:min-h-0 md:flex-none",
  desktop: "h-[400px] flex-none md:h-auto md:min-h-[300px] md:flex-1",
};

const CaseSliderGallerySection: React.FC<CaseSectionProps> = ({
  caseData,
  cmsData,
  cmsSection,
}) => {
  const modernLabels = newCaseLabelStyle(cmsData.theme.label);
  const nextOpenhouse = caseData.openhouses[0];
  const openHouseText = nextOpenhouse
    ? nextOpenhouse.bookingRequired
      ? `Åbent hus med tilmelding - ${getOpenHouseText(nextOpenhouse)}`
      : `Åbent hus - ${getOpenHouseText(nextOpenhouse)}`
    : undefined;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const fills = ownSectionHeights(cmsSection.ui);
  // A light section text colour must not reach the white boxes, which keep the page's text colour.
  const boxText = ownSectionTextColor(cmsSection)
    ? " text-[var(--base-text-color)]"
    : "";
  const slider = (
    <CaseDetailsImageSlider
      caseData={caseData}
      isFullscreen={isFullscreen}
      setIsFullscreen={setIsFullscreen}
      fontType={cmsData.theme.fontType}
      fill={fills !== "none"}
      labelOverlay={
        modernLabels ? (
          <div className="absolute inset-x-[15px] top-[15px] z-20 flex flex-col items-start gap-3 pointer-events-none">
            <CaseDetailLabels
              inline
              caseData={caseData}
              settings={cmsData.theme.label}
            />
            <div className="hidden lg:flex flex-col bg-white/70 p-4 text-[var(--base-text-color)]">
              <div className="homepage-text-xl">
                {caseData.advertisementAddressOrDisplayName}
              </div>
              {caseData.isActive && (
                <div className="homepage-text-lg">{caseData.priceInfo}</div>
              )}
            </div>
          </div>
        ) : undefined
      }
    >
      {!isFullscreen && !modernLabels && (
        <div className="flex flex-col homepage-text-sm">
          {!modernLabels && caseData?.marketingTexts?.label && (
            <div className=" homepage-text-sm uppercase bg-[var(--button-bg-color)] text-[var(--button-text-color)] border:border-[var(--button-border-color)] p-2 py-1 homepage-text-sm">
              {caseData?.marketingTexts?.label}
            </div>
          )}
          <div
            className={`bg-white font-bold bg-opacity-45 p-4 flex flex-col${boxText}`}
          >
            <div className="homepage-text-xl">
              {caseData.advertisementAddressOrDisplayName}
            </div>
            {caseData.isActive && (
              <div className="homepage-text-lg">{caseData.priceInfo}</div>
            )}
          </div>
          {!modernLabels &&
            caseData.isActive &&
            caseData.openhouses.length > 0 && (
              <div className={`bg-white p-4 flex flex-col gap-2${boxText}`}>
                <div className="flex justify-between items-center gap-10">
                  <span>{openHouseText}</span>
                  <button
                    aria-label="download calendar appointment"
                    onClick={() => {
                      const openhouse = caseData.openhouses[0];
                      const icsContent = createIcsEvent(
                        openHouseText!,
                        new Date(openhouse.startDate!),
                        new Date(openhouse.endDate!),
                        openhouse.location,
                      );
                      const blob = new Blob([icsContent], {
                        type: "text/calendar",
                      });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <FontAwesomeIcon
                      className="size-5"
                      icon={faCalendarCirclePlus}
                    />
                  </button>
                </div>
                <div>
                  {caseData.openhouses.find((x) => x.bookingRequired) && (
                    <RippleCtaButton
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent("selectedTabEvent", {
                            detail: { selectedTab: "openhouse" },
                          }),
                        );
                        document
                          .getElementById("caseTabsComponent")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      Tilmeld
                    </RippleCtaButton>
                  )}
                </div>
              </div>
            )}
        </div>
      )}
    </CaseDetailsImageSlider>
  );
  const content = (
    <>
      <div
        className={
          fills === "none"
            ? "lg:hidden flex flex-1 flex-col"
            : "lg:hidden flex flex-none flex-col"
        }
      >
        {!modernLabels && caseData?.marketingTexts?.label && (
          <div className=" homepage-text-sm uppercase bg-[var(--button-bg-color)] text-[var(--button-text-color)] border-[var(--button-border-color)] p-2 py-1 homepage-text-sm">
            {caseData?.marketingTexts?.label}
          </div>
        )}
        {!modernLabels && getCaseDetailSublabel(caseData) && (
          <div className=" homepage-text-sm uppercase bg-[var(--button-bg-color)] text-[var(--button-text-color)] border-[var(--button-border-color)] p-2 py-1 homepage-text-sm">
            <span>{getCaseDetailSublabel(caseData)}</span>
          </div>
        )}
        <div className="font-bold flex flex-col p-2">
          <div className="homepage-text-lg">
            {caseData.advertisementAddressOrDisplayName}
          </div>
          {caseData.isActive && (
            <div className="text-header-level-3">{caseData.priceInfo}</div>
          )}
        </div>
        {!modernLabels &&
          caseData.isActive &&
          caseData.openhouses.length > 0 && (
            <div className={`bg-white p-4 flex flex-col gap-2${boxText}`}>
              <div className="flex justify-between items-center gap-10">
                <span>{openHouseText}</span>
                <button
                  aria-label="download calendar appointment"
                  onClick={() => {
                    const openhouse = caseData.openhouses[0];
                    const icsContent = createIcsEvent(
                      openHouseText!,
                      new Date(openhouse.startDate!),
                      new Date(openhouse.endDate!),
                      openhouse.location,
                    );
                    const blob = new Blob([icsContent], {
                      type: "text/calendar",
                    });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <FontAwesomeIcon
                    className="size-5"
                    icon={faCalendarCirclePlus}
                  />
                </button>
              </div>
              <div>
                {caseData.openhouses.find((x) => x.bookingRequired) && (
                  <RippleCtaButton
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent("selectedTabEvent", {
                          detail: { selectedTab: "openhouse" },
                        }),
                      );
                      document
                        .getElementById("caseTabsComponent")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Tilmeld
                  </RippleCtaButton>
                )}
              </div>
            </div>
          )}
      </div>

      <div className={`relative flex flex-col ${SLIDER_HEIGHT[fills]}`}>
        {fills === "none" ? (
          slider
        ) : (
          // Out of flow, so the photos' own size does not make the slider taller than the height it is given.
          <div className="absolute inset-0">{slider}</div>
        )}
      </div>
    </>
  );
  return (
    <SectionLayout
      cmsSection={getDefaultCmsSection(cmsSection, { padding: "none" })}
      ownHeight="fill"
    >
      {fills === "none" ? (
        content
      ) : (
        <div className="flex min-w-0 flex-col">{content}</div>
      )}
    </SectionLayout>
  );
};

export default CaseSliderGallerySection;
